import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Upload, Sparkles, Save, Trash2, Plus, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";

const BE = process.env.REACT_APP_BACKEND_URL || "";
const src = (u) => (u && u.startsWith("/api") ? BE + u : u);
const SIZES = [["wide", "Wide 16:9"], ["square", "Square 1:1"], ["tall", "Tall 4:5"]];

export default function AdminHolidayBanner() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");

  const load = () => { setLoading(true); api.get("/admin/holiday-banner").then((r) => setCfg(r.data)).catch(() => setCfg({ slides: [] })).finally(() => setLoading(false)); };
  useEffect(load, []);

  const updSlide = (id, patch) => setCfg((c) => ({ ...c, slides: c.slides.map((s) => s.id === id ? { ...s, ...patch } : s) }));

  const [dragIdx, setDragIdx] = useState(null);
  const reorder = async (from, to) => {
    if (from == null || to == null || from === to || to < 0 || to >= (cfg.slides || []).length) return;
    const arr = [...cfg.slides];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setCfg((c) => ({ ...c, slides: arr }));
    try {
      await api.put("/admin/holiday-banner", {
        enabled: !!cfg.enabled, interval_ms: Number(cfg.interval_ms) || 5000,
        slides: arr.map((s) => ({ id: s.id, heading: s.heading || "", subheading: s.subheading || "", cta_label: s.cta_label || "", cta_link: s.cta_link || "" })),
      });
      toast.success("Order updated");
    } catch { toast.error("Reorder save failed"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/holiday-banner", {
        enabled: !!cfg.enabled, interval_ms: Number(cfg.interval_ms) || 5000,
        slides: cfg.slides.map((s) => ({ id: s.id, heading: s.heading || "", subheading: s.subheading || "", cta_label: s.cta_label || "", cta_link: s.cta_link || "" })),
      });
      setCfg(r.data); toast.success("Carousel saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const addSlide = async () => {
    setBusy("add");
    try { const r = await api.post("/admin/holiday-banner/slide"); setCfg(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); } finally { setBusy(""); }
  };
  const removeSlide = async (id) => {
    if (!window.confirm("Remove this banner slide?")) return;
    setBusy(id + "-del");
    try { const r = await api.delete(`/admin/holiday-banner/slide/${id}`); setCfg(r.data); toast.success("Removed"); }
    catch { toast.error("Delete failed"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const slides = cfg.slides || [];

  return (
    <div data-testid="admin-holiday-banner-page" className="max-w-4xl">
      <PageHeader title="Holiday Packages Banner" icon={ImageIcon} subtitle="Rotate up to 3 promotional banners atop the customer Holiday Packages tab. Each slide takes its own uploaded or AI-generated image." />

      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" data-testid="banner-enabled">
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} /> Show carousel
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">Rotate every
          <input type="number" min={2} max={15} value={Math.round((Number(cfg.interval_ms) || 5000) / 1000)} onChange={(e) => setCfg((c) => ({ ...c, interval_ms: Math.max(2, Math.min(15, Number(e.target.value) || 5)) * 1000 }))} data-testid="banner-interval" className="h-8 w-16 rounded-lg border border-slate-200 px-2 text-sm" /> sec
        </label>
        <button onClick={save} disabled={saving} data-testid="banner-save" className="ml-auto inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all
        </button>
      </div>

      {slides.length > 1 && <p className="mb-2 text-xs text-slate-400">Drag the handle (or use ↑ ↓) to reorder how banners rotate.</p>}
      <div className="space-y-4">
        {slides.map((s, i) => <SlideCard key={s.id} slide={s} index={i} busy={busy} setBusy={setBusy} updSlide={updSlide} setCfg={setCfg} removeSlide={removeSlide} dragIdx={dragIdx} setDragIdx={setDragIdx} onReorder={reorder} slidesLen={slides.length} />)}
      </div>

      {slides.length < 3 && (
        <button onClick={addSlide} disabled={busy === "add"} data-testid="banner-add-slide" className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-fuchsia-300 hover:text-fuchsia-600 disabled:opacity-50">
          {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add banner slide ({slides.length}/3)
        </button>
      )}
    </div>
  );
}

function SlideCard({ slide, index, busy, setBusy, updSlide, setCfg, removeSlide, dragIdx, setDragIdx, onReorder, slidesLen }) {
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("wide");
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(slide.id + "-up");
    try { const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/admin/holiday-banner/slide/${slide.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCfg(r.data); toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const generate = async () => {
    if (prompt.trim().length < 3) { toast.error("Describe the banner"); return; }
    setBusy(slide.id + "-gen");
    try { const r = await api.post(`/admin/holiday-banner/slide/${slide.id}/generate`, { prompt, size }); setCfg(r.data); toast.success("AI image generated"); }
    catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setBusy(""); }
  };
  const upBusy = busy === slide.id + "-up", genBusy = busy === slide.id + "-gen";

  return (
    <div className={`rounded-2xl border bg-white p-4 transition ${dragIdx === index ? "border-fuchsia-400 opacity-60" : "border-slate-200"}`} data-testid={`banner-slide-${index}`}
      onDragOver={(e) => e.preventDefault()} onDrop={() => { onReorder(dragIdx, index); setDragIdx(null); }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span draggable onDragStart={() => setDragIdx(index)} onDragEnd={() => setDragIdx(null)} data-testid={`banner-drag-${index}`} title="Drag to reorder" className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing"><GripVertical className="h-4 w-4" /></span>
          <p className="text-sm font-bold text-slate-800">Slide {index + 1}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => onReorder(index, index - 1)} disabled={index === 0} data-testid={`banner-up-${index}`} title="Move up" className="rounded-full border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
          <button onClick={() => onReorder(index, index + 1)} disabled={index === slidesLen - 1} data-testid={`banner-down-${index}`} title="Move down" className="rounded-full border border-slate-200 p-1 text-slate-500 hover:bg-slate-50 disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
          <button onClick={() => removeSlide(slide.id)} data-testid={`banner-remove-${index}`} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"><Trash2 className="h-3 w-3" /> Remove</button>
        </div>
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <div className="relative overflow-hidden rounded-xl bg-slate-100" data-testid={`banner-slide-preview-${index}`}>
            {slide.image_url ? (
              <>
                <img src={src(slide.image_url)} alt="" className="h-40 w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                <div className="absolute inset-0 flex flex-col justify-center gap-0.5 p-4 text-white">
                  {slide.heading && <p className="max-w-[70%] text-lg font-bold drop-shadow">{slide.heading}</p>}
                  {slide.subheading && <p className="max-w-[70%] text-xs text-white/90">{slide.subheading}</p>}
                  {slide.cta_label && <span className="mt-1 w-fit rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-900">{slide.cta_label}</span>}
                </div>
              </>
            ) : <div className="flex h-40 items-center justify-center text-xs text-slate-400">No image yet</div>}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid={`banner-file-${index}`} />
            <button onClick={() => fileRef.current?.click()} disabled={upBusy} data-testid={`banner-upload-${index}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">{upBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload</button>
          </div>
          <div className="mt-2 rounded-xl border border-fuchsia-100 bg-fuchsia-50/40 p-2.5">
            <p className="inline-flex items-center gap-1 text-xs font-bold text-fuchsia-700"><Sparkles className="h-3.5 w-3.5" /> Generate with AI</p>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid={`banner-ai-prompt-${index}`} placeholder="e.g. Kerala backwaters houseboat at sunrise" className="mt-1.5 w-full rounded-lg border border-slate-200 p-2 text-xs outline-none focus:border-fuchsia-400" />
            <div className="mt-1.5 flex items-center gap-2">
              <select value={size} onChange={(e) => setSize(e.target.value)} data-testid={`banner-ai-size-${index}`} className="h-8 rounded-lg border border-slate-200 px-2 text-xs">{SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              <button onClick={generate} disabled={genBusy} data-testid={`banner-generate-${index}`} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-fuchsia-700 disabled:opacity-50">{genBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</> : <><Sparkles className="h-3.5 w-3.5" /> Generate</>}</button>
            </div>
          </div>
        </div>
        <div className="grid content-start gap-2">
          <label className="text-xs font-semibold text-slate-500">Heading<input value={slide.heading || ""} onChange={(e) => updSlide(slide.id, { heading: e.target.value })} maxLength={120} data-testid={`banner-heading-${index}`} className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-fuchsia-400" /></label>
          <label className="text-xs font-semibold text-slate-500">Sub-heading<input value={slide.subheading || ""} onChange={(e) => updSlide(slide.id, { subheading: e.target.value })} maxLength={240} data-testid={`banner-subheading-${index}`} className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-fuchsia-400" /></label>
          <label className="text-xs font-semibold text-slate-500">Button label<input value={slide.cta_label || ""} onChange={(e) => updSlide(slide.id, { cta_label: e.target.value })} maxLength={40} data-testid={`banner-cta-label-${index}`} className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-fuchsia-400" /></label>
          <label className="text-xs font-semibold text-slate-500">Button link<input value={slide.cta_link || ""} onChange={(e) => updSlide(slide.id, { cta_link: e.target.value })} maxLength={300} data-testid={`banner-cta-link-${index}`} placeholder="/holidays/goa" className="mt-1 h-9 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-fuchsia-400" /></label>
        </div>
      </div>
    </div>
  );
}
